import { consumeMessageJump } from '../../lib/pendingMessageJump';

import { sortOrderFromString } from '../core/chatItem';
import { isRecord } from '../core/record';
import type { FeedMessageJumpInput, FeedSession } from '../core/session';
import type { ChannelId } from '../core/types';
import { markFeedJumpStart } from './feedPerformance';

type MessageJumpSession = Pick<FeedSession, 'jumpToMessage'>;
type MessageJumpComplete = (input: FeedMessageJumpInput) => void;

interface MessageJumpOptions {
  readonly onJumpComplete?: MessageJumpComplete;
}

/** Consumes a pending route-level jump for the active channel and centers it if valid. */
export function drainPendingMessageJump(
  session: MessageJumpSession,
  channelId: string | null,
  consume: (channelId: string) => string | null = consumeMessageJump,
  options?: MessageJumpOptions
): boolean {
  if (!channelId) return false;

  const pendingSortOrder = consume(channelId);
  const sortOrder = pendingSortOrder ? sortOrderFromString(pendingSortOrder) : undefined;
  if (!sortOrder) return false;

  jumpToMessage(session, { sortOrder, align: 'center' }, options);
  return true;
}

/** Handles a window-level message-jump request for the currently active feed channel. */
export function handleFeedMessageJumpRequest(
  session: MessageJumpSession,
  activeChannelId: ChannelId | null,
  detail: unknown,
  consume: (channelId: string) => string | null = consumeMessageJump,
  options?: MessageJumpOptions
): boolean {
  if (!activeChannelId || !isRecord(detail)) return false;
  if (String(detail.channelId) !== activeChannelId) return false;

  const sortOrder = typeof detail.sortOrder === 'string' ? sortOrderFromString(detail.sortOrder) : undefined;
  if (!sortOrder) return false;

  consume(activeChannelId);
  jumpToMessage(
    session,
    {
      sortOrder,
      align: normalizeJumpAlign(detail.align)
    },
    options
  );
  return true;
}

function normalizeJumpAlign(value: unknown): 'start' | 'center' | 'end' {
  if (value === 'start' || value === 'end') return value;
  return 'center';
}

function jumpToMessage(
  session: MessageJumpSession,
  input: FeedMessageJumpInput,
  options?: MessageJumpOptions
): void {
  markFeedJumpStart();
  void session.jumpToMessage(input).then((jumped) => {
    if (jumped) options?.onJumpComplete?.(input);
  }).catch(reportMessageJumpError);
}

function reportMessageJumpError(error: unknown): void {
  console.error('[MessageFeed] message jump failed:', error);
}
